import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HiPencil, HiSave, HiPlus, HiTrash, HiClipboardCheck } from "react-icons/hi";
import api from "@/services/api";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

const CATEGORIES = [
  { key: "deliverables", name: "Deliverables", weight: 40 },
  { key: "skills", name: "Skills", weight: 30 },
  { key: "culture", name: "Culture", weight: 30 },
];

const categoryPalette = [
  { header: "bg-amber-500/10", text: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  { header: "bg-blue-500/10", text: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  { header: "bg-teal-500/10", text: "text-teal-400", bg: "bg-teal-500/10", border: "border-teal-500/30" },
];

function toCategories(review) {
  return CATEGORIES.map(({ key, name, weight }) => ({
    key,
    name,
    weight,
    average: review[key]?.average || 0,
    talent_bar: review[key]?.talent_bar,
    subcategories: JSON.parse(JSON.stringify(review[key]?.subcategories || [])),
  }));
}

function ScoreButtons({ value, onChange, label, criteria }) {
  const [showTooltip, setShowTooltip] = useState(null);
  const scores = [1, 2, 3, 4, 5];
  const scoreLabels = { 1: "Poor", 2: "Basic", 3: "Intermediate", 4: "Advanced", 5: "Exceptional" };
  const scoreColors = {
    1: "bg-red-500/30 border-red-500 text-red-700",
    2: "bg-yellow-500/30 border-yellow-500 text-yellow-700",
    3: "bg-blue-500/30 border-blue-500 text-blue-700",
    4: "bg-teal-500/30 border-teal-500 text-teal-700",
    5: "bg-purple-500/30 border-purple-500 text-purple-700",
  };
  const scoreEmojis = { 1: "🚩", 2: "⚠️", 3: "✓", 4: "⭐", 5: "🏆" };

  return (
    <div className="py-3 border-b border-gray-200 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {criteria && (
            <p className="text-xs text-gray-500 mt-0.5">
              {value ? `${scoreEmojis[value]} ${criteria[value]?.split("\n")[0] || ""}` : "Hover a score to see criteria"}
            </p>
          )}
        </div>
        <div className="flex gap-1 relative">
          {scores.map((score) => (
            <div key={score} className="relative">
              <button
                type="button"
                onClick={() => onChange(score)}
                onMouseEnter={() => setShowTooltip(score)}
                onMouseLeave={() => setShowTooltip(null)}
                className={`w-9 h-9 rounded-lg text-sm font-medium border transition-all ${
                  value === score ? scoreColors[score] : "bg-gray-100 border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-200"
                }`}>
                {score}
              </button>
              {showTooltip === score && criteria?.[score] && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-white border border-gray-300 rounded-lg shadow-xl z-50">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${scoreColors[score]}`}>
                    {scoreEmojis[score]} {scoreLabels[score]}
                  </span>
                  <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed mt-2">{criteria[score]}</p>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-white border-r border-b border-gray-300 rotate-45"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScoreDisplay({ value, label, criteria }) {
  const [expanded, setExpanded] = useState(false);
  const scoreColors = { 1: "text-red-600", 2: "text-yellow-600", 3: "text-blue-600", 4: "text-teal-600", 5: "text-purple-600" };
  const scoreBgColors = { 1: "bg-red-500/10", 2: "bg-yellow-500/10", 3: "bg-blue-500/10", 4: "bg-teal-500/10", 5: "bg-purple-500/10" };
  const scoreLabels = { 1: "Poor", 2: "Basic", 3: "Intermediate", 4: "Advanced", 5: "Exceptional" };
  const scoreEmojis = { 1: "🚩", 2: "⚠️", 3: "✓", 4: "⭐", 5: "🏆" };
  const rounded = Math.round(value || 0);
  const criteriaText = criteria?.[rounded];

  return (
    <div
      className={`py-2 border-b border-gray-200 last:border-b-0 transition-colors ${criteria ? "cursor-pointer" : ""} ${expanded ? scoreBgColors[rounded] : "hover:bg-gray-50"}`}
      onClick={() => criteria && setExpanded(!expanded)}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-700">{label}</p>
          {criteriaText && !expanded && (
            <p className="text-xs text-gray-500 mt-0.5">{scoreEmojis[rounded]} {criteriaText.split("\n")[0]}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${scoreColors[rounded] || "text-gray-600"}`}>
            {value?.toFixed(1) ?? "—"}
          </span>
          {criteria && <span className="text-xs text-gray-400">{expanded ? "▲" : "▼"}</span>}
        </div>
      </div>
      {expanded && criteriaText && (
        <div className={`mt-2 p-3 rounded-lg ${scoreBgColors[rounded]} border border-gray-200`}>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${scoreColors[rounded]} bg-white`}>
            {scoreEmojis[rounded]} {scoreLabels[rounded]} ({rounded})
          </span>
          <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed mt-2">{criteriaText}</p>
        </div>
      )}
    </div>
  );
}

function recalculateLocally(categories) {
  const updated = categories.map((cat) => {
    const values = cat.subcategories.filter((s) => s.value !== null && s.value !== undefined).map((s) => s.value);
    const average = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    return { ...cat, average };
  });

  const overall = updated.reduce((sum, cat) => sum + cat.average * (cat.weight / 100), 0);

  let grade = "Basic";
  if (overall >= 4.6) grade = "Exceptional";
  else if (overall >= 3.6) grade = "Advanced";
  else if (overall >= 2.6) grade = "Intermediate";
  else if (overall >= 1.6) grade = "Basic";
  else grade = "Poor";

  return { categories: updated, overall, grade };
}

function getGradeInfo(grade) {
  const info = {
    Exceptional: { emoji: "🏆", color: "purple" },
    Advanced: { emoji: "⭐", color: "teal" },
    Intermediate: { emoji: "✓", color: "blue" },
    Basic: { emoji: "⚠️", color: "yellow" },
    Poor: { emoji: "🚩", color: "red" },
  };
  return info[grade] || info.Basic;
}

function initEditedData(r) {
  return {
    seniority: r.seniority,
    mission: r.mission,
    status: r.status,
    categories: toCategories(r),
    overall: r.overall_score || 0,
    grade: r.overall_grade || "Basic",
    strengths: [...(r.strengths || [])],
    developmentAreas: [...(r.development_areas || [])],
    actionPlan: [...(r.action_plan || [])],
    managerNotes: r.manager_notes || "",
  };
}

function buildSavePayload(editedData) {
  const payload = {
    seniority: editedData.seniority,
    mission: editedData.mission,
    status: editedData.status,
    strengths: editedData.strengths,
    development_areas: editedData.developmentAreas,
    action_plan: editedData.actionPlan,
    manager_notes: editedData.managerNotes,
  };

  for (const { key, name } of CATEGORIES) {
    const cat = editedData.categories.find((c) => c.name === name);
    if (cat) payload[key] = { subcategories: cat.subcategories.map((s) => ({ name: s.name, value: s.value })) };
  }

  return payload;
}

export default function Assessment({ cycle }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { businessUnit } = useSelector((state) => state.Auth);
  const business_unit_id = businessUnit?._id;
  const quarter = cycle?.quarter;
  const year = cycle?.year;
  const [review, setReview] = useState(null);
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(function () {
    fetchProjects();
  }, []);

  async function fetchProjects() {
    try {
      const { data, ok } = await api.post("/project/search", { limit: 100 });
      if (!ok) return;
      setProjects(data.projects || []);
    } catch (error) {
      console.error("Failed to fetch projects", error);
    }
  }

  useEffect(() => {
    if (!business_unit_id || !quarter || !year) {
      setTeam([]);
      return;
    }
    fetchTeamReviews();
  }, [business_unit_id, quarter, year]);

  useEffect(
    function () {
      if (!userId || team.length === 0) return;
      const found = team.find((r) => r._id === userId || r.user_id === userId);
      if (found) {
        setReview(found);
        setEditedData(initEditedData(found));
      } else {
        setReview(null);
      }
    },
    [userId, team]
  );

  async function fetchTeamReviews() {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/performance_review/search", { business_unit_id, quarter, year });
      if (!ok) return toast.error("Failed to load reviews");
      setTeam(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }

  function handleScoreChange(categoryName, subcategoryName, value) {
    setEditedData((prev) => {
      const newCategories = prev.categories.map((cat) => {
        if (cat.name !== categoryName) return cat;
        return { ...cat, subcategories: cat.subcategories.map((s) => (s.name === subcategoryName ? { ...s, value } : s)) };
      });
      const { categories, overall, grade } = recalculateLocally(newCategories);
      return { ...prev, categories, overall, grade };
    });
  }

  function handleArrayChange(field, index, value) {
    setEditedData((prev) => {
      const updated = { ...prev };
      updated[field] = [...prev[field]];
      updated[field][index] = value;
      return updated;
    });
  }

  function handleArrayAdd(field) {
    setEditedData((prev) => ({ ...prev, [field]: [...(prev[field] || []), ""] }));
  }

  function handleArrayRemove(field, index) {
    setEditedData((prev) => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  }

  function isAllScoresFilled(categories) {
    return categories?.every((cat) => cat.subcategories.every((s) => s.value !== null && s.value !== undefined));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const { data, ok, code } = await api.put(`/performance_review/${review._id}`, buildSavePayload(editedData));
      if (!ok) return toast.error(code || "Failed to save");
      toast.success("Review saved!");
      setReview(data);
      setEditMode(false);
      fetchTeamReviews();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save review");
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete() {
    const cats = editMode ? editedData.categories : toCategories(review);
    if (!isAllScoresFilled(cats)) {
      toast.error("Please fill in all scores before marking as complete");
      return;
    }
    setSaving(true);
    try {
      const payload = editMode ? editedData : initEditedData(review);
      const { data, ok, code } = await api.put(`/performance_review/${review._id}`, { ...buildSavePayload(payload), status: "completed" });
      if (!ok) return toast.error(code || "Failed to complete review");
      toast.success("Review marked as completed!");
      setReview(data);
      setEditMode(false);
      fetchTeamReviews();
    } catch (error) {
      console.error(error);
      toast.error("Failed to complete review");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setEditedData(review ? initEditedData(review) : null);
    setEditMode(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Select Developer to Assess</h2>
          <div className="text-sm text-gray-600">
            {quarter} {year}
          </div>
        </div>

        {team.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center shadow-sm">
            <p className="text-gray-600 mb-4">No reviews found for this quarter.</p>
            <p className="text-sm text-gray-500">Create a new cycle from Settings to start assessing.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {team.map((r) => {
              const gradeInfo = getGradeInfo(r.overall_grade);
              return (
                <button
                  key={r._id}
                  onClick={() => navigate(`/performance/assess/${r._id}`)}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-500/50 transition-colors text-left shadow-sm">
                  <div className="flex items-center gap-3">
                    {r.user_avatar ? (
                      <img src={r.user_avatar} alt={r.user_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">{r.user_name?.charAt(0)}</div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{r.user_name}</p>
                      <p className="text-sm text-gray-600">{r.mission || "No mission"}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{r.seniority}</span>
                        {r.status === "completed" && <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">✓</span>}
                      </div>
                    </div>
                    {r.overall_score > 0 && (
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">{r.overall_score.toFixed(1)}</p>
                        <p className="text-xs text-gray-500">
                          {gradeInfo.emoji} {r.overall_grade}
                        </p>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (!review) return <div className="p-6 text-center text-gray-600">Review not found</div>;

  const displayData = editMode ? editedData : { ...initEditedData(review), overall: review.overall_score, grade: review.overall_grade };
  const gradeInfo = getGradeInfo(displayData.grade);
  const categories = displayData.categories || [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(`/performance/assess`)} className="text-gray-600 hover:text-gray-900 text-sm">
          ← Back to team
        </button>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {review.quarter} {review.year}
          </span>
          {editMode ? (
            <>
              <button onClick={handleCancel} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50">
                <HiSave className="w-4 h-4" />
                {saving ? "Saving..." : "Save Draft"}
              </button>
              {review.status !== "completed" && (
                <button onClick={handleComplete} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50">
                  <HiClipboardCheck className="w-4 h-4" />
                  {saving ? "Saving..." : "Save & Complete"}
                </button>
              )}
            </>
          ) : (
            <>
              {review.status === "completed" ? (
                <span className="text-sm text-green-600 bg-green-50 border border-green-200 px-3 py-2 rounded-lg flex items-center gap-1.5">
                  <HiClipboardCheck className="w-4 h-4" /> Completed
                </span>
              ) : (
                <button onClick={handleComplete} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50">
                  <HiClipboardCheck className="w-4 h-4" />
                  Mark as Complete
                </button>
              )}
              <button onClick={() => setEditMode(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors">
                <HiPencil className="w-4 h-4" />
                Edit Scores
              </button>
            </>
          )}
        </div>
      </div>

      {/* User Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center gap-4">
          {review.user_avatar ? (
            <img src={review.user_avatar} alt={review.user_name} className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-medium">{review.user_name?.charAt(0)}</div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{review.user_name}</h1>
            {editMode ? (
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Project:</label>
                  <select
                    value={editedData.mission || ""}
                    onChange={(e) => setEditedData({ ...editedData, mission: e.target.value })}
                    className="bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 focus:outline-none focus:border-blue-500 max-w-[200px]">
                    <option value="">Select project...</option>
                    {projects.map((p) => (
                      <option key={p._id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Seniority:</label>
                  <select
                    value={editedData.seniority || "Junior"}
                    onChange={(e) => setEditedData({ ...editedData, seniority: e.target.value })}
                    className="bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 focus:outline-none focus:border-blue-500">
                    <option value="Junior">Junior</option>
                    <option value="Mid">Mid</option>
                    <option value="Senior">Senior</option>
                    <option value="Lead">Lead</option>
                  </select>
                </div>
              </div>
            ) : (
              <>
                <p className="text-gray-600">{review.mission || "No mission assigned"}</p>
                <span className="inline-block mt-1 text-xs text-blue-600 border border-blue-200 bg-blue-50 px-2 py-0.5 rounded">{review.seniority}</span>
              </>
            )}
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-blue-600">{displayData.overall?.toFixed(2) || "—"}</p>
            <p className="text-sm text-gray-600">
              {gradeInfo.emoji} {displayData.grade}
            </p>
          </div>
        </div>
      </div>

      {/* Score Summary Card */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className={`grid grid-cols-4 gap-4`}>
          {categories.map((cat, idx) => {
            const palette = categoryPalette[idx % categoryPalette.length];
            return (
              <div key={cat.name} className={`${palette.bg} ${palette.border} border rounded-lg p-3`}>
                <h3 className={`text-xs font-medium ${palette.text} mb-1`}>{cat.name}</h3>
                <p className="text-xl font-bold text-gray-900">{cat.average?.toFixed(2) ?? "—"}</p>
                <p className="text-xs text-gray-500">{cat.weight}%</p>
              </div>
            );
          })}
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-lg p-3">
            <h3 className="text-xs font-medium text-blue-600 mb-1">Final Score</h3>
            <p className="text-xl font-bold text-gray-900">{displayData.overall?.toFixed(2) || "—"}</p>
            <p className="text-xs text-gray-600">{gradeInfo.emoji} {displayData.grade}</p>
          </div>
        </div>

        {/* Grade Scale Reference */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Performance Grade Scale:</p>
          <div className="flex gap-2 flex-wrap">
            <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-600">🚩 Poor (1.0-1.5)</span>
            <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-600">⚠️ Basic (1.6-2.5)</span>
            <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-600">✓ Intermediate (2.6-3.5)</span>
            <span className="text-xs px-2 py-1 rounded bg-teal-500/20 text-teal-600">⭐ Advanced (3.6-4.5)</span>
            <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-600">🏆 Exceptional (4.6-5.0)</span>
          </div>
        </div>

        {/* Talent Bar Comparison */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Talent Bar Comparison ({review.seniority}):</p>
          <p className="text-xs text-gray-600">
            vs. Actual:{" "}
            <span className={review.talent_bar_comparison?.includes("Below") ? "text-yellow-600" : "text-blue-600"}>
              {review.talent_bar_comparison || "—"}
            </span>
          </p>
        </div>
      </div>

      {/* Score Sections */}
      {categories.map((cat, idx) => {
        const palette = categoryPalette[idx % categoryPalette.length];
        return (
          <div key={cat.name} className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4 shadow-sm">
            <div className={`${palette.header} px-4 py-3 border-b border-gray-200 flex items-center justify-between`}>
              <h3 className={`font-medium ${palette.text}`}>
                {idx + 1}. {cat.name.toUpperCase()} ({cat.weight}%)
              </h3>
              <span className={`text-lg font-bold ${palette.text}`}>{cat.average?.toFixed(2) ?? "—"}</span>
            </div>
            <div className="p-4 space-y-1">
              {cat.subcategories.map((sub) =>
                editMode ? (
                  <ScoreButtons key={sub.name} label={sub.name} value={sub.value} criteria={sub.criteria} onChange={(v) => handleScoreChange(cat.name, sub.name, v)} />
                ) : (
                  <ScoreDisplay key={sub.name} label={sub.name} value={sub.value} criteria={sub.criteria} />
                )
              )}
            </div>
          </div>
        );
      })}

      {/* Qualitative Feedback */}
      <div className="space-y-4">
        {/* Strengths */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-blue-600">💪 Strengths</h3>
            {editMode && (
              <button onClick={() => handleArrayAdd("strengths")} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1">
                <HiPlus className="w-4 h-4" /> Add
              </button>
            )}
          </div>
          <div className="p-4 space-y-2">
            {editMode
              ? editedData.strengths?.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" value={s} onChange={(e) => handleArrayChange("strengths", i, e.target.value)} className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-blue-500" />
                    <button onClick={() => handleArrayRemove("strengths", i)} className="text-red-600 hover:text-red-700 p-1">
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))
              : displayData.strengths?.map((s, i) => (
                  <p key={i} className="text-sm text-gray-700">• {s}</p>
                ))}
            {(!displayData.strengths || displayData.strengths.length === 0) && !editMode && <p className="text-sm text-gray-500 italic">No strengths recorded</p>}
          </div>
        </div>

        {/* Development Areas */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-yellow-600">📈 Development Areas</h3>
            {editMode && (
              <button onClick={() => handleArrayAdd("developmentAreas")} className="text-yellow-600 hover:text-yellow-700 text-sm flex items-center gap-1">
                <HiPlus className="w-4 h-4" /> Add
              </button>
            )}
          </div>
          <div className="p-4 space-y-2">
            {editMode
              ? editedData.developmentAreas?.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" value={d} onChange={(e) => handleArrayChange("developmentAreas", i, e.target.value)} className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-blue-500" />
                    <button onClick={() => handleArrayRemove("developmentAreas", i)} className="text-red-600 hover:text-red-700 p-1">
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))
              : displayData.developmentAreas?.map((d, i) => (
                  <p key={i} className="text-sm text-gray-700">• {d}</p>
                ))}
            {(!displayData.developmentAreas || displayData.developmentAreas.length === 0) && !editMode && <p className="text-sm text-gray-500 italic">No development areas recorded</p>}
          </div>
        </div>

        {/* Action Plan */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-blue-600">🎯 Action Plan</h3>
            {editMode && (
              <button onClick={() => handleArrayAdd("actionPlan")} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1">
                <HiPlus className="w-4 h-4" /> Add
              </button>
            )}
          </div>
          <div className="p-4 space-y-2">
            {editMode
              ? editedData.actionPlan?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" value={a} onChange={(e) => handleArrayChange("actionPlan", i, e.target.value)} className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-blue-500" />
                    <button onClick={() => handleArrayRemove("actionPlan", i)} className="text-red-600 hover:text-red-700 p-1">
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))
              : displayData.actionPlan?.map((a, i) => (
                  <p key={i} className="text-sm text-gray-700">• {a}</p>
                ))}
            {(!displayData.actionPlan || displayData.actionPlan.length === 0) && !editMode && <p className="text-sm text-gray-500 italic">No action plan recorded</p>}
          </div>
        </div>
      </div>

      {/* Raw Data */}
      {(() => {
        const rawData = team.find((r) => r._id === userId || r.user_id === userId);
        if (!rawData) return null;
        return (
          <div className="mt-4">
            <button onClick={() => setShowRaw(!showRaw)} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
              {showRaw ? "▼" : "▶"} Raw Data
            </button>
            {showRaw && (
              <div className="mt-2 bg-gray-900 rounded-lg p-4">
                <pre className="text-white text-sm overflow-x-auto whitespace-pre-wrap">{JSON.stringify(rawData, null, 2)}</pre>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
